import React, { useCallback, useState, useEffect, useMemo } from "react";
import { ChartTimeFrame, useChartSettingsStore } from "@/lib/chart-settings";
import { useMainChartStore } from "@/lib/main-chart";
import { useChartIndicatorsStore } from "@/lib/chart-indicators";
import DrawingsScreen from "./chart-drawings/DrawingsScreen";
import { KLineChartSymbolSelector } from "./symbol-selector";
import { ChartTradePanel } from "./chart-trade-panel";
import Chart from "./Chart";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useChartPanelId } from "@/components/chart/ChartPanelContext";
import { ChartHeader } from "./chart-header";
import { ChartNoSymbolSelectedContent } from "./ChartNoSymbolSelectedContent";
import IndicatorsScreen from "./chart-indicators/IndicatorsScreen2";
import { useSelectedSymbolMarketDepthSubscription } from "@/vybe-trader-client/order-book";
import { preloadIndicatorRuntime } from "@/lib/custom-indicators/runtime/IndicatorRuntimeManager";
import VybeStudioDialog from "./chart-indicators/custom/VybeStudioDialog";
import { useVybeStudioStore } from "@/lib/custom-indicators/studio-store";
import { useChartDeskRestoreStore } from "@/lib/chart-desk/restore-store";

export interface CandlestickData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface ChartScreenProps {
  isDesktop?: boolean;
  isPopup?: boolean;
  /**
   * When false, this panel does not own the global market-depth subscription
   * (desktop multi-chart: only the focused panel should pass true).
   * @default true
   */
  ownsMarketDepthSubscription?: boolean;
}

export default function ChartScreen({
  isDesktop = false,
  isPopup = false,
  ownsMarketDepthSubscription = true,
}: ChartScreenProps) {
  // Get chart data from store

  const chartPanelId = useChartPanelId();
  const showStudio = useVybeStudioStore((s) => s.open);
  const setShowStudio = useVybeStudioStore((s) => s.setOpen);

  const [showIndicators, setShowIndicators] = useState(false);
  const [showObjects, setShowObjects] = useState(false);
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [isSpecificTradePanelVisible, setIsSpecificTradePanelVisible] =
    useState(false);
  const [isMarketDepthVisible, setIsMarketDepthVisible] = useState(false);

  const [isCrosshairVisible, setIsCrosshairVisible] = useState(false);

  const selectedSymbol = useChartStore3(
    (state) => state.slots[chartPanelId].selectedSymbol,
  );

  // Proposal 74: what this panel is waiting to have restored, if anything.
  const restoringSlot = useChartDeskRestoreStore(
    (state) => state.pending[chartPanelId] ?? null,
  );

  // Own the single MT5 market-depth subscription for the chart-selected symbol.
  // Visibility of the DOM panel does not influence subscription lifecycle.
  useSelectedSymbolMarketDepthSubscription({
    enabled: ownsMarketDepthSubscription,
    panelId: chartPanelId,
  });

  useEffect(() => {
    // Warm the .NET indicator runtime only after the boot-critical window: the
    // download is ~30MB of WASM/DLLs and competes with the SignalR connect,
    // symbol catalog, and first chart data for bandwidth. Anything that needs
    // it sooner (editor, studio, computeIndicator) ensures the host itself.
    const timer = setTimeout(() => preloadIndicatorRuntime(), 10_000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!selectedSymbol) {
      setIsPanelVisible(false);
      setIsSpecificTradePanelVisible(false);
      setIsMarketDepthVisible(false);
    }
  }, [selectedSymbol]);

  const handleObjectAdded = () => {
    setSelectedObjectType("");
  };

  const handleObjectSelected = (objectType: string) => {
    setSelectedObjectType(objectType);
    if (isDesktop) {
      setShowObjects(false); // Zamknij popover po wyborze obiektu
    }
  };

  // On mobile, the drawings/indicators screens are rendered as an
  // absolutely-positioned overlay ABOVE the chart rather than replacing the
  // whole tree. This keeps <Chart> mounted (mirroring the desktop popover
  // variant, which never unmounts the chart), avoiding a full klinecharts
  // dispose + reload round-trip just to toggle an indicator/drawing.
  const showMobileObjectsOverlay = showObjects && !isDesktop;
  const showMobileIndicatorsOverlay = showIndicators && !isDesktop;

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col bg-background text-foreground">
      <ChartHeader
        showIndicatorsScreen={() => setShowIndicators(true)}
        showObjectsScreen={() => setShowObjects(true)}
        isCrosshairVisible={isCrosshairVisible}
        setIsCrosshairVisible={setIsCrosshairVisible}
        isPanelVisible={isPanelVisible}
        setIsPanelVisible={setIsPanelVisible}
        isSpecificTradePanelVisible={isSpecificTradePanelVisible}
        setIsSpecificTradePanelVisible={setIsSpecificTradePanelVisible}
        isMarketDepthVisible={isMarketDepthVisible}
        setIsMarketDepthVisible={setIsMarketDepthVisible}
        isDesktop={isDesktop}
        isPopup={isPopup}
        showIndicatorsPopover={isDesktop ? showIndicators : false}
        onIndicatorsPopoverChange={isDesktop ? setShowIndicators : undefined}
        showObjectsPopover={isDesktop ? showObjects : false}
        onObjectsPopoverChange={isDesktop ? setShowObjects : undefined}
        selectedObjectType={selectedObjectType}
        onObjectSelected={handleObjectSelected}
      />

      {!isDesktop ? (
        <VybeStudioDialog
          open={showStudio}
          onOpenChange={setShowStudio}
          isDesktop={false}
          chartPanelId={chartPanelId}
        />
      ) : null}

      {!isDesktop && isPanelVisible && <ChartTradePanel />}

      {!selectedSymbol && (
        <div className="flex-1 min-h-0 overscroll-contain">
          <ChartNoSymbolSelectedContent
            isDesktop={isDesktop}
            restoring={restoringSlot}
          />
        </div>
      )}

      {selectedSymbol && (
        <div className="flex-1 min-h-0 overscroll-contain">
          <Chart
            selectedObjectType={selectedObjectType}
            onObjectAdded={handleObjectAdded}
            isCrosshairVisible={isCrosshairVisible}
            isSpecificTradePanelVisible={isSpecificTradePanelVisible}
            setIsSpecificTradePanelVisible={setIsSpecificTradePanelVisible}
            isDesktop={isDesktop}
            isTradePanelVisible={isPanelVisible}
            isMarketDepthVisible={isMarketDepthVisible}
          />
        </div>
      )}

      {/* Mobile overlay layers — Chart stays mounted underneath */}
      {showMobileObjectsOverlay && (
        <div className="absolute inset-0 z-20 flex flex-col bg-background text-foreground">
          <DrawingsScreen
            onBack={() => setShowObjects(false)}
            setSelectedDrawingType={setSelectedObjectType}
            isDesktop={isDesktop}
          />
        </div>
      )}

      {showMobileIndicatorsOverlay && (
        <div className="absolute inset-0 z-20 flex flex-col bg-background text-foreground">
          <IndicatorsScreen
            onBack={() => setShowIndicators(false)}
            isDesktop={isDesktop}
          />
        </div>
      )}
    </div>
  );
}
